import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building2, Hospital, Stethoscope, Fingerprint } from 'lucide-react';
import { api } from '../api';

const TYPE_ICON = {
  facility:  { Icon: Building2,   color: '#4A7C5E', label: 'SNF' },
  hospital:  { Icon: Hospital,    color: '#34538F', label: 'Hospital' },
  physician: { Icon: Stethoscope, color: '#A06628', label: 'Physician' },
};

const ROUTE_FOR = (it) => {
  if (it.entity_type === 'facility')  return `/facilities/${it.entity_id}`;
  if (it.entity_type === 'hospital')  return `/hospitals/${it.entity_id}`;
  if (it.entity_type === 'physician') return `/physicians/${it.entity_id}`;
  return '/';
};

/**
 * Global cross-entity search: NPI / CCN / DBA / Legal name / facility-or-hospital-or-physician name.
 * Renders a compact input + popover. Used in Navbar.
 */
export default function GlobalEntitySearch({ className = '' }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!q || q.trim().length < 2) {
      setItems([]); setGroups({}); return;
    }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      api.entitySearch(q.trim(), 8)
        .then((d) => { setItems(d.items || []); setGroups(d.groups || {}); })
        .catch(() => { setItems([]); setGroups({}); })
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  const go = (it) => { setOpen(false); setQ(''); navigate(ROUTE_FOR(it)); };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <div className="flex items-center bg-white border border-[#E5DCC4] rounded-md px-2 h-9 w-56 focus-within:border-[#4A7C5E]">
        <Search size={13} className="text-[#7A857F] shrink-0" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="NPI · CCN · DBA · name"
          className="bg-transparent text-[12.5px] px-2 flex-1 outline-none placeholder:text-[#A8B0AA]"
          data-testid="global-entity-search-input"
        />
      </div>
      {open && q.trim().length >= 2 && (
        <div
          className="absolute right-0 mt-1.5 w-[420px] bg-[#FBF6EC] border border-[#E5DCC4] rounded-lg shadow-xl z-50 overflow-hidden"
          data-testid="global-entity-search-results"
        >
          <div className="px-3 py-2 flex items-center justify-between border-b border-[#E5DCC4] bg-[#F5EFDD]">
            <span className="text-[10.5px] uppercase tracking-wider text-[#5A6660] font-semibold">
              {loading ? 'Searching…' : `${items.length} result${items.length === 1 ? '' : 's'}`}
            </span>
            <div className="flex items-center gap-2">
              {Object.entries(groups).map(([t, n]) => {
                const meta = TYPE_ICON[t];
                if (!meta) return null;
                return (
                  <span key={t} className="text-[10px] text-[#5A6660] flex items-center gap-1">
                    <meta.Icon size={10} style={{ color: meta.color }} /> {n}
                  </span>
                );
              })}
            </div>
          </div>
          {items.length === 0 && !loading && (
            <div className="px-4 py-6 text-center text-[12px] text-[#7A857F]">
              No matches. Try a CCN, 10-digit NPI, or a name fragment.
            </div>
          )}
          <ul className="max-h-[380px] overflow-y-auto">
            {items.map((it, i) => {
              const meta = TYPE_ICON[it.entity_type] || { Icon: Fingerprint, color: '#7A857F', label: '—' };
              return (
                <li key={`${it.entity_type}-${it.entity_id}-${i}`}>
                  <button
                    onClick={() => go(it)}
                    className="w-full text-left px-3 py-2.5 flex items-start gap-2.5 hover:bg-[#F5EFDD] border-b last:border-b-0 border-[#F0E7D2]"
                    data-testid={`global-search-result-${it.entity_type}-${it.entity_id}`}
                  >
                    <meta.Icon size={14} className="mt-0.5 shrink-0" style={{ color: meta.color }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-semibold text-[#1A2622] truncate">
                        {it.label || '(no name)'}
                      </div>
                      <div className="text-[10.5px] text-[#7A857F] truncate">{it.sub}</div>
                    </div>
                    <span
                      className="text-[9.5px] uppercase font-semibold px-1.5 py-0.5 rounded mt-0.5 shrink-0"
                      style={{ background: '#F5EFDD', color: meta.color }}
                    >
                      {meta.label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="px-3 py-1.5 text-[10px] text-[#A8B0AA] border-t border-[#E5DCC4] bg-[#F5EFDD]">
            Powered by NPPES + CMS public registries · cross-entity match
          </div>
        </div>
      )}
    </div>
  );
}
